/**
 * Bulk-add rules from a CSV: download the template, fill it in, upload it,
 * review what each row will do, then import.
 */

import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, CheckCircle2, Download, FileSpreadsheet, Upload, X } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { useImportRules } from '../../../hooks/queries/useRules';
import { ImportResult, ImportRowAction, ruleService } from '../../../services/api/ruleService';

const MAX_FILE_BYTES = 2 * 1024 * 1024;

const ACTION_STYLE: Record<ImportRowAction, { label: string; badge: string }> = {
  create: { label: 'New', badge: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' },
  update: { label: 'Update', badge: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' },
  unchanged: { label: 'No change', badge: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300' },
  skip: { label: 'Skipped', badge: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300' },
  error: { label: 'Error', badge: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' }
};

const ORDER: ImportRowAction[] = ['error', 'create', 'update', 'unchanged', 'skip'];

export const ImportRulesModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { token } = useAuth();
  const importRules = useImportRules();
  const fileInput = useRef<HTMLInputElement>(null);

  const [fileName, setFileName] = useState<string | null>(null);
  const [csv, setCsv] = useState<string | null>(null);
  const [preview, setPreview] = useState<ImportResult | null>(null);
  const [done, setDone] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  const busy = importRules.isPending;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, busy]);

  const downloadTemplate = async () => {
    if (!token) return;
    setError(null);
    setDownloading(true);
    try {
      await ruleService.downloadTemplate(token);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setDownloading(false);
    }
  };

  const chooseFile = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    setPreview(null);
    setDone(null);
    if (!/\.(csv|txt)$/i.test(file.name)) {
      setError('Choose a .csv file. In Excel, use File > Save As > CSV UTF-8.');
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      setError('The file is larger than 2 MB.');
      return;
    }
    const text = await file.text();
    setFileName(file.name);
    setCsv(text);
    importRules.mutate({ csv: text, apply: false }, {
      onSuccess: result => setPreview(result as ImportResult),
      onError: e => setError(e.message)
    });
  };

  const runImport = () => {
    if (!csv) return;
    setError(null);
    importRules.mutate({ csv, apply: true }, {
      onSuccess: result => setDone(result as ImportResult),
      onError: e => setError(e.message)
    });
  };

  const reset = () => {
    setFileName(null);
    setCsv(null);
    setPreview(null);
    setDone(null);
    setError(null);
    if (fileInput.current) fileInput.current.value = '';
  };

  const toImport = preview ? preview.counts.create + preview.counts.update : 0;
  const rows = preview
    ? [...preview.rows].sort((a, b) => ORDER.indexOf(a.action) - ORDER.indexOf(b.action) || a.line - b.line)
    : [];

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => !busy && onClose()}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="import-rules-title"
        onClick={e => e.stopPropagation()}
        className="w-full max-w-3xl max-h-[90vh] flex flex-col rounded-2xl bg-white dark:bg-dark-surface shadow-xl"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-dark-border">
          <h2 id="import-rules-title" className="text-lg font-semibold text-gray-900 dark:text-white">Import rules from CSV</h2>
          <button type="button" onClick={onClose} disabled={busy} aria-label="Close" className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-dark-bg-secondary">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {done ? (
            <div className="text-center py-6">
              <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-500" />
              <h3 className="mt-3 text-base font-medium text-gray-900 dark:text-white">Import complete</h3>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                {done.created} new rule{done.created === 1 ? '' : 's'} added, {done.updated} updated.
                {done.counts.error > 0 && ` ${done.counts.error} row${done.counts.error === 1 ? '' : 's'} with errors ${done.counts.error === 1 ? 'was' : 'were'} left out.`}
              </p>
              {!!done.failures?.length && (
                <ul className="mt-4 text-left text-sm text-red-700 dark:text-red-300 space-y-1">
                  {done.failures.map(f => <li key={f.line}>Line {f.line} ({f.title}): {f.error}</li>)}
                </ul>
              )}
            </div>
          ) : (
            <>
              <ol className="grid sm:grid-cols-2 gap-3 text-sm">
                <li className="rounded-xl border border-gray-200 dark:border-dark-border p-4">
                  <div className="font-medium text-gray-900 dark:text-white">1. Get the template</div>
                  <p className="mt-1 text-gray-600 dark:text-gray-400">
                    One rule per row. Severity is Critical, High risk or Negotiate. Applies to is "all" or contract
                    types separated by semicolons. Rows starting "Example:" are ignored.
                  </p>
                  <button
                    type="button"
                    onClick={downloadTemplate}
                    disabled={downloading}
                    className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium border border-gray-300 dark:border-dark-border text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-dark-bg-secondary"
                  >
                    <Download className="w-4 h-4" /> {downloading ? 'Downloading...' : 'Download template'}
                  </button>
                </li>
                <li className="rounded-xl border border-gray-200 dark:border-dark-border p-4">
                  <div className="font-medium text-gray-900 dark:text-white">2. Upload it</div>
                  <p className="mt-1 text-gray-600 dark:text-gray-400">
                    Save as CSV (in Excel: CSV UTF-8). You'll see what each row does before anything is saved.
                    Rules with the same title as an existing rule update it.
                  </p>
                  <input
                    ref={fileInput}
                    type="file"
                    accept=".csv,text/csv"
                    className="sr-only"
                    id="rule-csv-file"
                    onChange={e => chooseFile(e.target.files?.[0])}
                  />
                  <label
                    htmlFor="rule-csv-file"
                    className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium bg-primary-600 hover:bg-primary-700 text-white dark:bg-accent-500 dark:text-gray-900 cursor-pointer"
                  >
                    <Upload className="w-4 h-4" /> {fileName ? 'Choose another file' : 'Choose CSV file'}
                  </label>
                </li>
              </ol>

              {fileName && (
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <FileSpreadsheet className="w-4 h-4" /> {fileName}
                  {busy && !preview && <span>· checking...</span>}
                </div>
              )}

              {preview && preview.fileErrors.length > 0 && (
                <div role="alert" className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-4 py-3 text-sm text-red-700 dark:text-red-300">
                  {preview.fileErrors.map(e => <p key={e}>{e}</p>)}
                </div>
              )}

              {preview && preview.fileErrors.length === 0 && (
                <div className="space-y-3">
                  <div className="flex flex-wrap gap-2 text-xs">
                    {ORDER.filter(a => preview.counts[a] > 0).map(a => (
                      <span key={a} className={`px-2.5 py-1 rounded-full font-semibold ${ACTION_STYLE[a].badge}`}>
                        {preview.counts[a]} {ACTION_STYLE[a].label.toLowerCase()}
                      </span>
                    ))}
                  </div>

                  {rows.length === 0 ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400">The file has no rules in it.</p>
                  ) : (
                    <div className="rounded-xl border border-gray-200 dark:border-dark-border overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 dark:bg-dark-bg-secondary text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                          <tr>
                            <th className="text-left font-semibold px-3 py-2 w-14">Line</th>
                            <th className="text-left font-semibold px-3 py-2">Rule</th>
                            <th className="text-left font-semibold px-3 py-2 w-28">Result</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-dark-border">
                          {rows.map(row => (
                            <tr key={row.line} className={row.action === 'error' ? 'bg-red-50/50 dark:bg-red-900/10' : ''}>
                              <td className="px-3 py-2 align-top text-gray-500">{row.line}</td>
                              <td className="px-3 py-2 align-top">
                                <div className="font-medium text-gray-900 dark:text-white">{row.title || <span className="italic text-gray-400">No title</span>}</div>
                                {row.errors.map(e => (
                                  <div key={e} className="mt-0.5 flex items-start gap-1 text-red-700 dark:text-red-300">
                                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /> {e}
                                  </div>
                                ))}
                                {row.note && <div className="mt-0.5 text-gray-500 dark:text-gray-400">{row.note}</div>}
                              </td>
                              <td className="px-3 py-2 align-top">
                                <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${ACTION_STYLE[row.action].badge}`}>
                                  {ACTION_STYLE[row.action].label}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-gray-100 dark:border-dark-border">
          {error ? (
            <p role="alert" className="text-sm text-red-600 dark:text-red-400">{error}</p>
          ) : preview && !done && preview.counts.error > 0 && toImport > 0 ? (
            <p className="text-xs text-gray-500 dark:text-gray-400">Rows with errors will be left out. Fix them and import again any time.</p>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            {done ? (
              <>
                <button type="button" onClick={reset} className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-bg-secondary">
                  Import another file
                </button>
                <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-medium bg-primary-600 hover:bg-primary-700 text-white dark:bg-accent-500 dark:text-gray-900">
                  Done
                </button>
              </>
            ) : (
              <>
                <button type="button" onClick={onClose} disabled={busy} className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-bg-secondary">
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={runImport}
                  disabled={busy || !preview || toImport === 0}
                  className="px-4 py-2 rounded-lg text-sm font-medium bg-primary-600 hover:bg-primary-700 text-white dark:bg-accent-500 dark:hover:bg-accent-400 dark:text-gray-900 disabled:opacity-50"
                >
                  {busy && preview ? 'Importing...' : toImport > 0 ? `Import ${toImport} rule${toImport === 1 ? '' : 's'}` : 'Import'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ImportRulesModal;
